import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Search, Truck, Check, Clock, PackageCheck } from "lucide-react";
import { useLang } from "../i18n";
import { IMAGES } from "../data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const L = {
    en: {
        kicker: "Follow your attar", title: "Track your order", body: "Enter the phone number you ordered with. Add your order number to narrow it down.",
        phone: "Phone / WhatsApp", ref: "Order number (optional)", phRef: "e.g. 998E9179", btn: "Track", searching: "Searching…",
        notFound: "No order found for these details.", total: "Total", shipped: "Shipped via", awb: "Tracking no.", courierPage: "Courier tracking page",
        placed: "Order placed", status: { cod_received: "Order received · Cash on delivery", awaiting_payment: "Awaiting payment", paid: "Paid · Preparing your parcel", shipped: "Shipped · On its way", delivered: "Delivered", cancelled: "Cancelled" },
    },
    fr: {
        kicker: "Suivez votre attar", title: "Suivre ma commande", body: "Saisissez le numéro de téléphone utilisé pour la commande. Ajoutez le numéro de commande pour affiner.",
        phone: "Téléphone / WhatsApp", ref: "Numéro de commande (facultatif)", phRef: "ex. 998E9179", btn: "Suivre", searching: "Recherche…",
        notFound: "Aucune commande trouvée.", total: "Total", shipped: "Expédié via", awb: "N° de suivi", courierPage: "Page de suivi du transporteur",
        placed: "Commande passée", status: { cod_received: "Commande reçue · Paiement à la livraison", awaiting_payment: "En attente de paiement", paid: "Payée · Préparation du colis", shipped: "Expédiée · En route", delivered: "Livrée", cancelled: "Annulée" },
    },
    ar: {
        kicker: "تابع عطرك", title: "تتبع طلبك", body: "أدخل رقم الهاتف الذي طلبت به. أضف رقم الطلب لتحديد النتيجة.",
        phone: "الهاتف / واتساب", ref: "رقم الطلب (اختياري)", phRef: "مثال 998E9179", btn: "تتبع", searching: "جارٍ البحث…",
        notFound: "لم يتم العثور على طلب بهذه البيانات.", total: "الإجمالي", shipped: "تم الشحن عبر", awb: "رقم التتبع", courierPage: "صفحة تتبع شركة الشحن",
        placed: "تم الطلب", status: { cod_received: "تم استلام الطلب · الدفع عند الاستلام", awaiting_payment: "بانتظار الدفع", paid: "مدفوع · جارٍ تجهيز الطرد", shipped: "تم الشحن · في الطريق", delivered: "تم التوصيل", cancelled: "ملغي" },
    },
    bn: {
        kicker: "আপনার আতর অনুসরণ করুন", title: "অর্ডার ট্র্যাক করুন", body: "যে ফোন নম্বর দিয়ে অর্ডার করেছেন সেটি লিখুন। নির্দিষ্ট করতে অর্ডার নম্বর যোগ করুন।",
        phone: "ফোন / হোয়াটসঅ্যাপ", ref: "অর্ডার নম্বর (ঐচ্ছিক)", phRef: "যেমন 998E9179", btn: "ট্র্যাক", searching: "খোঁজা হচ্ছে…",
        notFound: "এই তথ্যে কোনো অর্ডার পাওয়া যায়নি।", total: "মোট", shipped: "পাঠানো হয়েছে", awb: "ট্র্যাকিং নম্বর", courierPage: "কুরিয়ার ট্র্যাকিং পেজ",
        placed: "অর্ডার করা হয়েছে", status: { cod_received: "অর্ডার গৃহীত · ক্যাশ অন ডেলিভারি", awaiting_payment: "পেমেন্টের অপেক্ষায়", paid: "পরিশোধিত · পার্সেল প্রস্তুত হচ্ছে", shipped: "পাঠানো হয়েছে · পথে আছে", delivered: "ডেলিভারি সম্পন্ন", cancelled: "বাতিল" },
    },
};

const ICON = { paid: Check, shipped: Truck, delivered: PackageCheck };

const Field = ({ label, ...props }) => (
    <label className="block">
        <span className="text-[10px] tracking-[0.25em] uppercase text-ash block mb-1">{label}</span>
        <input {...props} className="w-full bg-transparent border-b border-charcoal/20 focus:border-gold outline-none py-2 text-sm text-charcoal transition-colors duration-300" />
    </label>
);

const OrderResult = ({ o, l }) => {
    const Icon = ICON[o.status] ?? Clock;
    return (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="border border-gold/40 bg-cream/40 p-8 text-left" data-testid={`track-result-${o.ref}`}>
            <div className="flex justify-between items-start border-b border-gold/20 pb-5 mb-5">
                <div>
                    <p className="text-[10px] tracking-[0.3em] uppercase text-golddark mb-1">#{o.ref}</p>
                    <p className="font-serif text-2xl text-charcoal flex items-center gap-3" data-testid={`track-status-${o.ref}`}>
                        <Icon size={20} strokeWidth={1.5} className="text-golddark" /> {l.status[o.status] ?? o.status}
                    </p>
                </div>
                <p className="text-xs text-ash">{l.placed}<br />{new Date(o.created_at).toLocaleDateString()}</p>
            </div>
            <ul className="space-y-2 mb-4">
                {o.items.map((it, i) => (
                    <li key={i} className="flex justify-between text-sm"><span className="text-charcoal">{it.qty} × {it.name}</span><span className="font-serif text-charcoal">₹{it.price * it.qty}</span></li>
                ))}
            </ul>
            <div className="flex justify-between items-baseline border-t border-gold/20 pt-3">
                <span className="text-[10px] tracking-[0.25em] uppercase text-smoke">{l.total}</span>
                <span className="font-serif text-xl text-charcoal">₹{o.total}</span>
            </div>
            {o.shipment?.awb_code && (
                <div className="mt-6 border-t border-gold/20 pt-5 text-sm space-y-1.5" data-testid={`track-shipment-${o.ref}`}>
                    <p className="text-charcoal">{l.shipped} <span className="font-medium">{o.shipment.courier}</span></p>
                    <p className="text-smoke">{l.awb} <span className="text-charcoal">{o.shipment.awb_code}</span></p>
                    {o.tracking && (
                        <p className="text-golddark" data-testid={`track-live-${o.ref}`}>{o.tracking.status}{o.tracking.etd ? ` · ETA ${o.tracking.etd}` : ""}</p>
                    )}
                    {o.tracking?.activities?.length > 0 && (
                        <ul className="pt-2 space-y-1 text-xs text-smoke">
                            {o.tracking.activities.slice(0, 5).map((a, i) => <li key={i}>{a.date} — {a.activity}{a.location ? ` (${a.location})` : ""}</li>)}
                        </ul>
                    )}
                    <a href={o.shipment.tracking_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-xs tracking-[0.2em] uppercase text-golddark underline underline-offset-4 hover:text-charcoal">{l.courierPage}</a>
                </div>
            )}
        </motion.div>
    );
};

export default function TrackPage() {
    const { lang } = useLang();
    const l = L[lang] ?? L.en;
    const [phone, setPhone] = useState("");
    const [ref, setRef] = useState("");
    const [busy, setBusy] = useState(false);
    const [orders, setOrders] = useState(null);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        setOrders(null);
        try {
            const { data } = await axios.post(`${API}/track`, { phone, ref: ref || null });
            setOrders(data.orders);
        } catch (err) {
            setError(err.response?.status === 404 ? l.notFound : typeof err.response?.data?.detail === "string" ? err.response.data.detail : l.notFound);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-ivory px-6 pt-32 pb-24" data-testid="track-page">
            <div className="max-w-xl mx-auto text-center">
                <img src={IMAGES.logo} alt="Al Zahra" className="w-16 mix-blend-multiply mx-auto mb-8" />
                <p className="text-[10px] tracking-[0.3em] uppercase text-golddark mb-3">{l.kicker}</p>
                <h1 className="font-serif text-4xl sm:text-5xl text-charcoal mb-5">{l.title}</h1>
                <p className="text-sm text-smoke leading-relaxed max-w-md mx-auto mb-12">{l.body}</p>

                <form onSubmit={submit} className="border border-gold/40 bg-cream/40 p-8 text-left space-y-6 mb-10" data-testid="track-form">
                    <Field label={l.phone} data-testid="track-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" placeholder="+91 …" />
                    <Field label={l.ref} data-testid="track-ref-input" value={ref} onChange={(e) => setRef(e.target.value.toUpperCase())} placeholder={l.phRef} />
                    <button data-testid="track-submit-button" disabled={busy || phone.replace(/\D/g, "").length < 10} className="w-full flex items-center justify-center gap-2 bg-charcoal text-goldlight px-8 py-4 text-xs tracking-[0.25em] uppercase hover:bg-golddark hover:text-ivory transition-colors duration-300 disabled:opacity-50">
                        <Search size={13} strokeWidth={1.5} /> {busy ? l.searching : l.btn}
                    </button>
                    {error && <p className="text-sm text-[#8B2E2E] text-center" data-testid="track-error">{error}</p>}
                </form>

                {orders && <div className="space-y-6" data-testid="track-results">{orders.map((o) => <OrderResult key={o.ref} o={o} l={l} />)}</div>}
            </div>
        </div>
    );
}
